/**
 * Pure presentation rules of the concierge screens and chat cards (CONCIERGE_CHAT_FIRST.md §5.1–§5.7) — kept out of
 * the components so they are unit-tested and decided in one place.
 */
import type {
  ConciergeListItem, ConciergeRequestView, ConciergeStatus, MethodFields, PaymentBody, PaymentClientView, PaymentDraft,
  RefundToClientView,
} from '@/api/concierge';
import type { SourceKind } from '@/api/topup';
import type { TermsBlockState } from '@/api/support';

/** Where the payments tab leads: the concierge screen when the client may create requests OR has some (§11.1). */
export function paymentsTabRoute(caps: { conciergePayments?: boolean; conciergeHistory?: boolean } | null):
  'payments' | 'topup' {
  return caps && (caps.conciergePayments || caps.conciergeHistory) ? 'payments' : 'topup';
}

/** i18n key of the shown status. */
export function statusLabelKey(status: ConciergeStatus | string): string {
  return `vitrine.concierge.status.${status}`;
}

/** Pill tone: in progress / done / stopped — the same three tones the top-up list uses. */
export function statusTone(status: ConciergeStatus | string): 'go' | 'done' | 'bad' {
  if (status === 'TERMS_AGREED' || status === 'COMPLETED') return 'done';
  if (status === 'CANCELLED' || status === 'REJECTED' || status === 'PAYMENT_FAILED') return 'bad';
  return 'go';
}

export function isTerminal(status: ConciergeStatus | string): boolean {
  return status === 'CANCELLED' || status === 'REJECTED';
}

/** Shown statuses in which no payment is current — the only ones where the request itself may be cancelled (§10.2). */
const CANCELLABLE_REQUEST = new Set(['SUBMITTED', 'NEEDS_INFO', 'TERMS_AGREED']);

/** «Отменить заявку» — only without a current payment (otherwise the server answers CONCIERGE_PAYMENT_IN_PROGRESS). */
export function canCancelRequest(req: Pick<ConciergeRequestView, 'status' | 'payment'>): boolean {
  return CANCELLABLE_REQUEST.has(req.status) && !req.payment;
}

/** A finished task: cancelled, rejected, or sent to the recipient. */
const FINISHED = new Set(['CANCELLED', 'REJECTED', 'COMPLETED']);

/**
 * «Повторить задачу» (the next tranche — a new request with a copy of the brief) — the creating action, so the caller
 * also checks the mode (REQ-032). Only on a finished task (CONCIERGE_CHAT_FIRST.md §5.7, §5.8): a task in progress is
 * continued in its own chat.
 */
export function canRepeat(status: ConciergeStatus | string): boolean {
  return FINISHED.has(status);
}

/** The one crypto pair a concierge payment accepts in v1 (D24): USDT on TRON. */
export const CONCIERGE_CRYPTO = { asset: 'USDT', network: 'TRON', networkLabel: 'TRC-20' } as const;

/**
 * Methods of the pay form, in the top-up order. CRYPTO only while the server offers it (`conciergeCrypto`, §6.4) —
 * and then only as the one pair above; no other asset or network is ever shown.
 */
export function payKindsFor(conciergeCrypto: boolean | null | undefined): SourceKind[] {
  return conciergeCrypto ? ['BANK', 'CASH', 'CRYPTO'] : ['BANK', 'CASH'];
}

/**
 * The form on a method the screen does not offer (a manager's CRYPTO draft while the crypto pipeline is off) moves to
 * the first offered one, keeping what was typed — otherwise the client would face a form without its chip.
 */
export function payFormForKinds(form: PayForm, kinds: SourceKind[], fallbackCurrency: string): PayForm {
  return kinds.includes(form.sourceKind) || !kinds.length ? form : switchPayKind(form, kinds[0], fallbackCurrency);
}

/** The canonical bank payer (D-2): the client themselves — a constant, never a question on the screen. */
export const SENDER_TYPE_DEFAULT = 'INDIVIDUAL';

/** The concierge pay form as the inputs hold it (strings straight from the fields). */
export interface PayForm {
  sourceKind: SourceKind;
  currency: string;
  bankName: string;
  bankCountry: string;
  senderType: string;
  /** null — the manager's draft left it open; the client has not answered yet. */
  accountOwnedByClient: boolean | null;
  accountLast4: string;
  country: string;
  city: string;
  availableFrom: string;
  availableTo: string;
  comment: string;
}

export function emptyPayForm(sourceKind: SourceKind, currency: string): PayForm {
  return {
    sourceKind,
    currency: sourceKind === 'CRYPTO' ? CONCIERGE_CRYPTO.asset : currency,
    bankName: '', bankCountry: '', senderType: SENDER_TYPE_DEFAULT, accountOwnedByClient: true, accountLast4: '',
    country: '', city: '', availableFrom: '', availableTo: '', comment: '',
  };
}

/** The form opened on the manager's draft (С-7) — or empty on the recipient's currency when there is none. */
export function payFormFromDraft(draft: PaymentDraft | null | undefined, fallbackCurrency: string): PayForm {
  if (!draft) return emptyPayForm('BANK', fallbackCurrency);
  const f: MethodFields = draft.fields ?? {};
  return {
    sourceKind: draft.sourceKind,
    currency: draft.sourceKind === 'CRYPTO' ? CONCIERGE_CRYPTO.asset : (draft.currency ?? fallbackCurrency),
    bankName: f.bankName ?? '',
    bankCountry: f.bankCountry ?? '',
    senderType: f.senderType ?? SENDER_TYPE_DEFAULT,
    accountOwnedByClient: f.accountOwnedByClient ?? null,
    accountLast4: f.accountLast4 ?? '',
    country: f.country ?? '',
    city: f.city ?? '',
    availableFrom: f.availableFrom ?? '',
    availableTo: f.availableTo ?? '',
    comment: f.comment ?? '',
  };
}

/** Switching the method keeps what was typed; only the currency follows the method (crypto is one fixed asset). */
export function switchPayKind(form: PayForm, kind: SourceKind, fallbackCurrency: string): PayForm {
  if (form.sourceKind === kind) return form;
  const currency = kind === 'CRYPTO'
    ? CONCIERGE_CRYPTO.asset
    : (form.sourceKind === 'CRYPTO' ? fallbackCurrency : form.currency);
  return { ...form, sourceKind: kind, currency };
}

/**
 * The first missing/invalid field (an i18n key), or null when the form can be sent. The same requirements the server
 * checks (PaymentDraftValidator, the top-up submit): bank — its country; cash — the country; a currency; the last
 * four, when given, are four digits. `kinds` — the methods the screen offers (payKindsFor); a method outside them is
 * refused the way the server would (CONCIERGE_METHOD_NOT_ALLOWED).
 */
export function payFormProblem(form: PayForm, kinds?: SourceKind[]): string | null {
  if (kinds && !kinds.includes(form.sourceKind)) return 'vitrine.error.CONCIERGE_METHOD_NOT_ALLOWED';
  // the server's rule (ShowcaseTopUpService.validateConciergeMethod: ^[A-Z]{3,8}$) — never stricter than it
  if (form.sourceKind !== 'CRYPTO' && !/^[A-Za-z]{3,8}$/.test(form.currency.trim())) {
    return 'vitrine.concierge.pay.needCurrency';
  }
  if (form.sourceKind === 'BANK') {
    if (!form.bankCountry.trim()) return 'vitrine.topup.needBankCountry';
    const last4 = form.accountLast4.trim();
    if (last4 && !/^\d{4}$/.test(last4)) return 'vitrine.concierge.pay.badLast4';
  }
  if (form.sourceKind === 'CASH' && !form.country.trim()) return 'vitrine.concierge.pay.needCountry';
  return null;
}

function text(v: string | null | undefined): string | null {
  const s = (v ?? '').trim();
  return s ? s : null;
}

function code(v: string | null | undefined): string | null {
  const s = text(v);
  return s ? s.toUpperCase() : null;
}

/**
 * The method fields as the server stores them (PaymentDraftValidator): trimmed text, upper-case codes, blanks as
 * null, only the chosen method's fields, a bank payer INDIVIDUAL unless named. Applied to both the form and the
 * draft, so «paid the draft as is» compares equal on both ends.
 */
function normalizeFields(kind: SourceKind, f: MethodFields | null | undefined): MethodFields {
  const src = f ?? {};
  const comment = text(src.comment);
  if (kind === 'BANK') {
    return {
      bankCountry: code(src.bankCountry), bankName: text(src.bankName),
      senderType: code(src.senderType) ?? SENDER_TYPE_DEFAULT, accountOwnedByClient: src.accountOwnedByClient ?? null,
      accountLast4: text(src.accountLast4),
      country: null, city: null, availableFrom: null, availableTo: null, comment,
    };
  }
  if (kind === 'CASH') {
    return {
      bankCountry: null, bankName: null, senderType: null, accountOwnedByClient: null, accountLast4: null,
      country: code(src.country), city: text(src.city), availableFrom: text(src.availableFrom),
      availableTo: text(src.availableTo), comment,
    };
  }
  return {
    bankCountry: null, bankName: null, senderType: null, accountOwnedByClient: null, accountLast4: null,
    country: null, city: null, availableFrom: null, availableTo: null, comment,
  };
}

/**
 * The wire body of «Оплатить» (§10.2): no amount (the recipient's amount is in the terms on the server), the request
 * version the screen showed, and the draft's version when the form was opened on one — the server compares the
 * values itself and answers CONCIERGE_DRAFT_CHANGED when the manager changed the draft meanwhile.
 */
export function toPaymentBody(form: PayForm, version: number, draftVersion: string | null): PaymentBody {
  const crypto = form.sourceKind === 'CRYPTO';
  return {
    sourceKind: form.sourceKind,
    currency: crypto ? CONCIERGE_CRYPTO.asset : form.currency.trim().toUpperCase(),
    network: crypto ? CONCIERGE_CRYPTO.network : null,
    fields: normalizeFields(form.sourceKind, form),
    version,
    draftVersion,
  };
}

/**
 * A card's call was refused because the card is stale (HTTP 409: the terms, the draft, the payment or the refund
 * details changed under it) — the card re-reads itself silently; any other refusal is shown as text under the button
 * (CONCIERGE_CHAT_FIRST.md §5.2 «Отказ сервера на кнопке»).
 */
export function isStaleRefusal(e: unknown): boolean {
  return (e as { response?: { status?: unknown } })?.response?.status === 409;
}

/**
 * The manager's prefilled pay-in method of a terms card (its edition's draft), as the pay form reads it; null when
 * the edition has none (then «Подтвердить условия» opens the method choice).
 */
export function termsCardDraft(s: Pick<TermsBlockState, 'method' | 'payInCurrency' | 'network' | 'methodFields'>):
  PaymentDraft | null {
  if (!s.method) return null;
  return {
    sourceKind: s.method,
    currency: s.payInCurrency ?? (s.method === 'CRYPTO' ? CONCIERGE_CRYPTO.asset : ''),
    network: s.network ?? null,
    fields: s.methodFields ?? null,
  };
}

/** The server's code of a refused call (`{error: CODE}`), or null. */
export function errorCodeOf(e: unknown): string | null {
  const v = (e as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof v === 'string' && /^[A-Z][A-Z0-9_]+$/.test(v) ? v : null;
}

/** «К оплате N, в т.ч. комиссия M%» — null until TH quoted («Сумму к оплате пришлёт менеджер»). */
export function askOf(p: PaymentClientView | null | undefined):
  { amount: number | string; currency: string; feePercent: number | string | null } | null {
  if (!p || p.askAmount == null) return null;
  return { amount: p.askAmount, currency: p.askCurrency ?? p.payInCurrency, feePercent: p.feePercent ?? null };
}

/** Payment (TopUpStatus) states before the money reached TH — the client still owes the transfer. */
const BEFORE_FUNDS = new Set(['SUBMITTED', 'SCREENING', 'APPROVED', 'EXPORTED', 'IN_PROGRESS']);

/** The client still has to pay: before the money arrived, and not after they said «Я отправил». */
export function awaitsFunding(p: PaymentClientView | null | undefined): boolean {
  return !!p && BEFORE_FUNDS.has(p.status) && !p.markedSent;
}

/**
 * «Реквизиты готовит менеджер» — no requisites on screen while the client still owes the transfer. Not after
 * «Я отправил» (held requisites are hidden again — a client who paid must not read «you still have to pay») and not
 * while TH holds the payment («Платёж приостановлен» says it).
 */
export function awaitsPayIn(p: PaymentClientView | null | undefined): boolean {
  return !!p && !p.payInInstructions && awaitsFunding(p) && !p.onHold;
}

/** Which buttons the payment block offers — the server's flags, never a guess from the status (§5.3, §7). */
export function paymentActions(p: PaymentClientView) {
  return {
    /** «Я отправил» — bank/cash only (for CRYPTO the server always refuses). */
    markSent: p.canMarkSent && p.sourceKind !== 'CRYPTO',
    cancel: p.cancellable && !p.cancelRequested,
    cancelRequested: p.cancelRequested,
    refund: p.refundRequestable,
    refundRequested: !p.refundRequestable && !!p.refundRequestedAt,
  };
}

/** `****1234` (the server's mask) → `•••• 1234`, the way the rest of the vitrine writes a masked account. */
export function displayMasked(masked: string | null | undefined): string {
  const v = (masked ?? '').trim();
  if (!v) return '';
  const tail = v.replace(/^[*•\s]+/, '');
  return tail ? `•••• ${tail}` : '••••';
}

/** The refund details for the client to check (§10.3.6), in reading order; empty fields left out. */
export function refundToLines(r: RefundToClientView | null | undefined): DraftLine[] {
  if (!r) return [];
  const lines: DraftLine[] = [];
  const push = (labelKey: string, value: string | null | undefined, extra: Partial<DraftLine> = {}) => {
    if (value != null && String(value).trim() !== '') lines.push({ labelKey, value: String(value).trim(), ...extra });
  };
  if (r.kind === 'BANK') {
    push('vitrine.concierge.refundTo.holder', r.holderName);
    push('vitrine.concierge.refundTo.account', displayMasked(r.accountMasked));
    push('vitrine.concierge.refundTo.swift', r.swiftBic);
    push('vitrine.concierge.refundTo.bank', r.bankName);
    push('vitrine.concierge.refundTo.bankCountry', r.bankCountry, { country: true });
  } else {
    push('vitrine.concierge.refundTo.city', r.city);
    push('vitrine.concierge.refundTo.pickupNote', r.pickupNote);
  }
  return lines;
}

/** One read-only line of the refund details: an i18n label key and either a value or a country code. */
export interface DraftLine {
  labelKey: string;
  value: string;
  /** The value is an ISO alpha-3 code to render through the localized country name. */
  country?: boolean;
  /** The value is an i18n key, not data. */
  i18n?: boolean;
}

// ── Entry: one field + files (CONCIERGE_CHAT_FIRST.md §5.1, CF-001) ──────────────────────────────────────────────

export const INTAKE_TEXT_MAX = 4000;
export const INTAKE_FILE_MAX_BYTES = 20 * 1024 * 1024;
export const INTAKE_TOTAL_MAX_BYTES = 60 * 1024 * 1024;
/** What the server accepts by signature: PDF, JPEG, PNG, WebP. HEIC and the rest are refused on the client. */
const INTAKE_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
export const INTAKE_ACCEPT = INTAKE_TYPES.join(',');
/** Some pickers give no MIME type — then the extension decides. */
const INTAKE_EXTENSION = /\.(pdf|jpe?g|png|webp)$/i;

/** Why this file cannot join the `picked` ones (an i18n key), or null. Files go as they are — nothing is re-encoded. */
export function intakeFileProblem(file: Pick<File, 'name' | 'size' | 'type'>, picked: Pick<File, 'size'>[]): string | null {
  const typeOk = file.type ? INTAKE_TYPES.includes(file.type) : INTAKE_EXTENSION.test(file.name);
  if (!typeOk) return 'vitrine.concierge.intake.error.type';
  if (file.size > INTAKE_FILE_MAX_BYTES) return 'vitrine.concierge.intake.error.tooLarge';
  const total = picked.reduce((sum, f) => sum + f.size, file.size);
  if (total > INTAKE_TOTAL_MAX_BYTES) return 'vitrine.concierge.intake.error.totalTooLarge';
  return null;
}

/** «Отправить» is on with a non-blank text OR at least one file. */
export function canSendIntake(text: string, files: unknown[]): boolean {
  return text.trim().length > 0 || files.length > 0;
}

// ── «Задачи»: filters, title, stage (§5.1, §5.6, CF-016, CF-034) ─────────────────────────────────────────────────

export type TaskFilter = 'all' | 'active' | 'done';

/** «В работе» / «Завершённые» is the server's `open` (isTaskOpen) — never a guess from the stage. */
export function filterTasks<T extends Pick<ConciergeListItem, 'open'>>(items: T[], filter: TaskFilter): T[] {
  if (filter === 'all') return items;
  return items.filter(i => i.open === (filter === 'active'));
}

const TITLE_TEXT_MAX = 65;

/**
 * CF-016: the recipient of the current terms → the first 65 characters of the task text with «…» → the first file's
 * name → null (the caller shows the localized «Новая задача»).
 */
export function taskTitle(item: Pick<ConciergeListItem, 'recipient' | 'textHead' | 'firstFileName'>): string | null {
  const recipient = item.recipient?.trim();
  if (recipient) return recipient;
  const text = item.textHead?.trim().replace(/\s+/g, ' ');
  if (text) return text.length > TITLE_TEXT_MAX ? `${text.slice(0, TITLE_TEXT_MAX).trimEnd()}…` : text;
  return item.firstFileName?.trim() || null;
}

/** «TC-<номер>» (CF-024); empty while the server has no number. */
export function taskNo(displayNo: number | string | null | undefined): string {
  return displayNo == null || displayNo === '' ? '' : `TC-${displayNo}`;
}

const STAGES = new Set(['REVIEW', 'NEEDS_INFO', 'AGREED', 'PREPARING', 'TO_PAY', 'CHECKING', 'SENT', 'FAILED',
  'REFUND', 'CANCELLED', 'REJECTED']);

/** i18n key of a client stage (`TO_PAY` → `vitrine.concierge.stage.toPay`); null for a stage this build does not know. */
export function stageLabelKey(stage: string | null | undefined): string | null {
  if (!stage || !STAGES.has(stage)) return null;
  const camel = stage.toLowerCase().replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
  return `vitrine.concierge.stage.${camel}`;
}

/** Pill tone of a stage — the three tones of the design: agreed and sent are green (as the shown status tone). */
export function stageTone(stage: string | null | undefined): 'go' | 'done' | 'bad' {
  if (stage === 'SENT' || stage === 'AGREED') return 'done';
  if (stage === 'CANCELLED' || stage === 'REJECTED' || stage === 'FAILED') return 'bad';
  return 'go';
}
